import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import {
  Users,
  UserCircle,
  Search,
  ChevronRight,
  AlertCircle,
  UserPlus,
  UserCheck,
  ClipboardCheck,
  TrendingUp,
  Calendar,
} from 'lucide-react';
import { Button } from '../../components/ui';
import { AddPatientModal } from '../../components/patients';

export function PatientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Stats for non-super admins
  const [onboardingStats, setOnboardingStats] = useState([]);
  const [testOutStats, setTestOutStats] = useState([]);

  const isSuperAdmin = user?.acl_care?.acl_care_superadmin === true;
  const hasLoggedAudit = useRef(false);

  // Fetch patients function (for super admins)
  const fetchPatients = async (shouldAudit = true) => {
    try {
      setLoading(true);
      setError(null);

      const result = await api.getAllACLUsers();

      const users = result.users || result || [];
      // Filter to only patients (has patient_id)
      const patientUsers = users.filter(u => u.patient_id != null);
      setPatients(patientUsers);

      // Audit: Patient list viewed (only once per mount)
      if (shouldAudit && !hasLoggedAudit.current) {
        hasLoggedAudit.current = true;
        api.createAuditLog({
          event_type: 'PATIENT_LIST_VIEWED',
          user_id: user?.id,
          user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email,
          description: 'User viewed the patients list',
          outcome: 'success',
          organization_id: user?.organization_id,
          additional_data: {
            patient_count: patientUsers.length,
            is_super_admin: isSuperAdmin
          }
        }).catch(() => {
          // Silently fail - audit logging should not block user actions
        });
      }

    } catch (err) {
      console.error('[PatientsPage] Error fetching patients:', err);
      setError('Failed to load patients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats for non-super admins
  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [onboardingResult, testOutResult] = await Promise.all([
        api.getOnboardingStats(user.organization_id),
        api.getTestOutStats(user.organization_id),
      ]);

      setOnboardingStats(onboardingResult || []);
      setTestOutStats(testOutResult || []);

      // Audit: Stats viewed
      if (!hasLoggedAudit.current) {
        hasLoggedAudit.current = true;
        api.createAuditLog({
          event_type: 'CLIENT_STATS_VIEWED',
          user_id: user?.id,
          user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email,
          description: 'User viewed client statistics',
          outcome: 'success',
          organization_id: user?.organization_id,
        }).catch(() => {});
      }

    } catch (err) {
      console.error('[PatientsPage] Error fetching stats:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount based on user type
  useEffect(() => {
    if (isSuperAdmin) {
      fetchPatients();
    } else {
      fetchStats();
    }
    return () => { hasLoggedAudit.current = false; };
  }, [isSuperAdmin]);

  // Filter patients by search (super admin only)
  const filteredPatients = useMemo(() => {
    if (!searchQuery.trim()) return patients;

    const query = searchQuery.toLowerCase();
    return patients.filter(u => {
      const name = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [patients, searchQuery]);

  // Compute onboarding statistics
  const computedOnboardingStats = useMemo(() => {
    const completed = onboardingStats.filter(
      u => u.acl_care?.acl_onboarding_completed_at
    );
    const total = onboardingStats.length;

    // Group by month
    const byMonth = {};
    completed.forEach(u => {
      const date = new Date(u.acl_care.acl_onboarding_completed_at);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      byMonth[key] = (byMonth[key] || 0) + 1;
    });

    // Sort months descending
    const monthlyBreakdown = Object.entries(byMonth)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, count]) => ({
        month,
        label: formatMonthLabel(month),
        count,
      }));

    return {
      completedCount: completed.length,
      totalCount: total,
      monthlyBreakdown,
    };
  }, [onboardingStats]);

  // Compute test out statistics
  const computedTestOutStats = useMemo(() => {
    const totalAttempts = testOutStats.length;
    const totalPassed = testOutStats.filter(t => t.overall_passed).length;
    const overallPassRate = totalAttempts > 0
      ? Math.round((totalPassed / totalAttempts) * 100)
      : 0;

    // Group by phase
    const byPhase = {};
    testOutStats.forEach(t => {
      const phase = t.phase || 1;
      if (!byPhase[phase]) {
        byPhase[phase] = { attempts: 0, passed: 0 };
      }
      byPhase[phase].attempts++;
      if (t.overall_passed) {
        byPhase[phase].passed++;
      }
    });

    const phaseBreakdown = Object.entries(byPhase)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([phase, data]) => ({
        phase: Number(phase),
        attempts: data.attempts,
        passed: data.passed,
        passRate: data.attempts > 0
          ? Math.round((data.passed / data.attempts) * 100)
          : 0,
      }));

    // Group by month
    const byMonth = {};
    testOutStats.forEach(t => {
      const timestamp = t.completed_at || t.created_at;
      const date = new Date(timestamp);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!byMonth[key]) {
        byMonth[key] = { attempts: 0, passed: 0 };
      }
      byMonth[key].attempts++;
      if (t.overall_passed) {
        byMonth[key].passed++;
      }
    });

    const monthlyBreakdown = Object.entries(byMonth)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([month, data]) => ({
        month,
        label: formatMonthLabel(month),
        attempts: data.attempts,
        passed: data.passed,
        passRate: data.attempts > 0
          ? Math.round((data.passed / data.attempts) * 100)
          : 0,
      }));

    return {
      totalAttempts,
      totalPassed,
      overallPassRate,
      phaseBreakdown,
      monthlyBreakdown,
    };
  }, [testOutStats]);

  const handlePatientClick = (patient) => {
    navigate(`/clients/${patient.id}`);
  };

  const handleAddPatient = () => {
    setShowAddModal(true);
  };

  const handlePatientAdded = () => {
    // Refresh the list after adding a patient (don't re-audit)
    fetchPatients(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4 flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">
            {isSuperAdmin ? 'Loading clients...' : 'Loading statistics...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Non-super admin: Show summary statistics
  if (!isSuperAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
        {/* Header */}
        <div className="flex items-center mb-6">
          <Users className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        </div>

        <div className="space-y-6">
          {/* Onboarding Stats Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-gray-900">Onboarding</h2>
              </div>
            </div>

            <div className="p-4">
              {/* Summary */}
              <div className="flex items-center gap-3 mb-4">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-xl font-bold text-primary">
                    {computedOnboardingStats.completedCount}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Users Completed Onboarding
                  </p>
                  <p className="text-sm text-gray-500">
                    out of {computedOnboardingStats.totalCount} total
                  </p>
                </div>
              </div>

              {/* Monthly Breakdown */}
              {computedOnboardingStats.monthlyBreakdown.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Monthly Breakdown</span>
                  </div>
                  <div className="space-y-2">
                    {computedOnboardingStats.monthlyBreakdown.map(({ month, label, count }) => (
                      <div key={month} className="flex justify-between items-center py-1">
                        <span className="text-sm text-gray-600">{label}</span>
                        <span className="text-sm font-medium text-gray-900">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {computedOnboardingStats.completedCount === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No onboarding data yet
                </p>
              )}
            </div>
          </div>

          {/* Test Out Stats Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-gray-900">Test Outs</h2>
              </div>
            </div>

            <div className="p-4">
              {computedTestOutStats.totalAttempts > 0 ? (
                <>
                  {/* Overall Summary */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {computedTestOutStats.totalPassed} of {computedTestOutStats.totalAttempts} passed
                      </p>
                      <p className="text-sm text-gray-500">
                        {computedTestOutStats.overallPassRate}% overall pass rate
                      </p>
                    </div>
                  </div>

                  {/* By Phase */}
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">By Phase</h3>
                    <div className="space-y-3">
                      {computedTestOutStats.phaseBreakdown.map(({ phase, attempts, passed, passRate }) => (
                        <div key={phase} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                              Phase {phase}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-gray-900">
                              {passed}/{attempts} passed
                            </span>
                            <span className="text-sm text-gray-500 ml-2">
                              ({passRate}%)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Monthly Breakdown */}
                  {computedTestOutStats.monthlyBreakdown.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700">Monthly Breakdown</span>
                      </div>
                      <div className="space-y-2">
                        {computedTestOutStats.monthlyBreakdown.map(({ month, label, attempts, passed, passRate }) => (
                          <div key={month} className="flex justify-between items-center py-1">
                            <span className="text-sm text-gray-600">{label}</span>
                            <div className="text-right">
                              <span className="text-sm font-medium text-gray-900">
                                {passed}/{attempts}
                              </span>
                              <span className="text-sm text-gray-500 ml-2">
                                ({passRate}%)
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  No test out data yet
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Super admin: Show patient list
  return (
    <div className="max-w-4xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Users className="h-6 w-6 text-primary mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        </div>
        {/* removing for now
        <Button onClick={handleAddPatient}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Client
        </Button>
        */}
      </div>

      {/* Patient List Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Search */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search clients..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-gray-400"
            />
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {filteredPatients.length} client{filteredPatients.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Patient List */}
        <div className="divide-y divide-gray-100">
          {filteredPatients.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <UserCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No clients found.</p>
              {searchQuery && (
                <p className="text-sm mt-1">Try adjusting your search</p>
              )}
            </div>
          ) : (
            filteredPatients.map((patient) => (
              <PatientListItem
                key={patient.id}
                patient={patient}
                onClick={() => handlePatientClick(patient)}
              />
            ))
          )}
        </div>
      </div>

      {/* Add Patient Modal */}
      <AddPatientModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handlePatientAdded}
        organizationId={user?.organization_id}
      />
    </div>
  );
}

// Helper function to format month label
function formatMonthLabel(monthKey) {
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function PatientListItem({ patient, onClick }) {
  const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Unknown';
  const initials = `${(patient.first_name || '?')[0]}${(patient.last_name || '?')[0]}`.toUpperCase();

  const aclCare = patient.acl_care || {};
  const currentPhase = aclCare.current_phase;
  const hasStarted = aclCare.acl_onboarding_completed;

  return (
    <button
      onClick={onClick}
      className="w-full p-4 flex items-center hover:bg-gray-50 transition-colors text-left"
    >
      {/* Avatar */}
      {patient.profile_photo ? (
        <img
          src={patient.profile_photo}
          alt={fullName}
          className="h-12 w-12 rounded-full object-cover mr-4"
        />
      ) : (
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mr-4">
          <span className="text-primary font-semibold">{initials}</span>
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 truncate">{fullName}</p>
        <p className="text-sm text-gray-500 truncate">{patient.email}</p>

        {/* Patient status */}
        <div className="flex items-center gap-2 mt-1">
          {hasStarted ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              Phase {currentPhase || 1}
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              Not started
            </span>
          )}
          {aclCare.injury_side && (
            <span className="text-xs text-gray-500 capitalize">
              {aclCare.injury_side} knee
            </span>
          )}
        </div>
      </div>

      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
    </button>
  );
}
